import { BALANCE } from '../config/balance';
import { dist2 } from '../core/vec';
import type { DamageSource } from '../core/types';
import type { Enemy } from './enemies';
import type { Engine } from './engine';
import { rollDamage } from './build';

// 武器索引对齐 build.WEAPON_IDS：boomerang=0 staff=1 whip=2 arrow=3
const HIT_INTERVAL = [
  BALANCE.weapons.boomerang.hitCd,
  0.2,
  BALANCE.weapons.whip.life,
  0.2,
];

/** 查询半径余量：覆盖最大 Boss 半径（36）。 */
const QUERY_PAD = 40;

function damageEnemy(
  engine: Engine,
  e: Enemy,
  weaponIndex: number,
  base: number,
  kx: number,
  ky: number,
  knockback: number,
): void {
  const p = engine.player;
  const rolled = rollDamage(base, p.hpRatio, p.stats, engine.streams.fx);
  e.hp -= rolled.amount;
  e.flash = 0.08;
  engine.damageNumbers.spawn(e.x, e.y, rolled.amount, rolled.crit, engine.streams.fx);
  e.hitCds[weaponIndex] = HIT_INTERVAL[weaponIndex];
  const nl = Math.hypot(kx, ky);
  if (nl > 1e-6 && knockback > 0) {
    e.kx += (kx / nl) * knockback;
    e.ky += (ky / nl) * knockback;
  }
  engine.fx.hitSpark(e.x, e.y);
  engine.emit({
    type: 'hit', x: e.x, y: e.y,
    amount: rolled.amount, crit: rolled.crit,
  });
  if (e.hp <= 0) engine.registerKill(e);
}

/** 回旋镖 → 怪（按镖位局部查询）。 */
function boomerangHits(engine: Engine): void {
  const ctrl = engine.weaponSet.boomerang;
  const size = ctrl.cur.size;
  for (const b of ctrl.list) {
    const near = engine.enemyHash.query(b.x, b.y, size + QUERY_PAD, engine.hashScratch);
    for (const e of near) {
      if (!e.active || e.hitCds[0] > 0) continue;
      const rr = e.radius + size;
      if (dist2(b.x, b.y, e.x, e.y) <= rr * rr) {
        damageEnemy(
          engine, e, 0, ctrl.cur.damage,
          e.x - engine.player.x, e.y - engine.player.y,
          ctrl.cur.knockback,
        );
      }
    }
  }
}

/** 法杖 / 哨箭弹 → 怪（穿透不重复命中）。 */
function boltHits(engine: Engine): void {
  for (const b of engine.boltPool.all()) {
    if (!b.active) continue;
    const near = engine.enemyHash.query(b.x, b.y, b.size + QUERY_PAD, engine.hashScratch);
    for (const e of near) {
      if (!e.active || b.hits.includes(e.id)) continue;
      const rr = e.radius + b.size;
      if (dist2(b.x, b.y, e.x, e.y) > rr * rr) continue;
      damageEnemy(engine, e, b.kind === 'staff' ? 1 : 3, b.damage, b.vx, b.vy, 0);
      b.hits.push(e.id);
      b.pierce--;
      if (b.pierce < 0) {
        engine.boltPool.release(b);
        break;
      }
    }
  }
}

/** 鞭横扫 → 怪（处刑者处决残血）。 */
function swipeHits(engine: Engine): void {
  for (const s of engine.swipePool.all()) {
    if (!s.active) continue;
    const near = engine.enemyHash.query(s.x, s.y, s.range + QUERY_PAD, engine.hashScratch);
    for (const e of near) {
      if (!e.active || s.hits.includes(e.id)) continue;
      if (!inSwipeCheck(s, e.x, e.y)) continue;
      s.hits.push(e.id);
      if (s.execute > 0 && e.hp / e.maxHp < s.execute) {
        e.hp = 0;
        engine.registerKill(e);
      } else {
        damageEnemy(
          engine, e, 2, s.damage,
          e.x - s.x, e.y - s.y, s.knockback,
        );
      }
    }
  }
}

function inSwipeCheck(s: { x: number; y: number; dir: number; range: number; arc: number },
  ex: number, ey: number): boolean {
  const dx = ex - s.x;
  const dy = ey - s.y;
  if (Math.hypot(dx, dy) > s.range) return false;
  let da = Math.atan2(dy, dx) - s.dir;
  while (da > Math.PI) da -= Math.PI * 2;
  while (da < -Math.PI) da += Math.PI * 2;
  return Math.abs(da) <= s.arc / 2;
}

/** 全部玩家武器命中判定。 */
export function weaponHits(engine: Engine): void {
  boomerangHits(engine);
  boltHits(engine);
  swipeHits(engine);
}

/** 敌方弹道 → 玩家（来源 spit / boss 驱动死因）。 */
export function foeBoltHits(engine: Engine): void {
  const p = engine.player;
  for (const b of engine.foeBoltPool.all()) {
    if (!b.active) continue;
    const rr = b.radius + BALANCE.player.radius;
    const dx = b.x - p.x;
    const dy = b.y - p.y;
    if (dx * dx + dy * dy <= rr * rr) {
      engine.hurtPlayer(b.damage, b.source);
      engine.foeBoltPool.release(b);
    }
  }
}

/** 怪物接触玩家；冲锋中走 charge 来源。 */
export function enemyContact(engine: Engine): void {
  const p = engine.player;
  if (p.iframes > 0) return;

  const near = engine.enemyHash.query(
    p.x, p.y, BALANCE.player.radius + QUERY_PAD, engine.hashScratch,
  );
  for (const e of near) {
    if (!e.active) continue;
    const rr = e.radius + BALANCE.player.radius;
    if (dist2(e.x, e.y, p.x, p.y) > rr * rr) continue;

    const source: DamageSource =
      e.kind === 'charger' && e.state === 'charge'
        ? { type: 'charge', enemyId: e.id }
        : { type: 'contact', kind: e.kind, enemyId: e.id };

    engine.hurtPlayer(e.damage, source);
    break;
  }
}
